'use client';

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { DashboardFilters } from '@/types/dashboard';

interface FiltersContextValue {
  filters: DashboardFilters;
  setFilters: (filters: DashboardFilters) => void;
  updateFilters: (patch: Partial<DashboardFilters>) => void;
  resetFilters: () => void;
  // Reset a single filter field back to its default (null / false) without
  // touching the rest of the selection — used by the dismissible chip row.
  resetFilter: (key: keyof DashboardFilters) => void;
  activeCount: number;
}

// Default value per filter field. ``as_of_date`` is intentionally included
// here (default null) but ``resetFilters`` preserves it — it's a snapshot
// selection, not a data filter.
const FILTER_DEFAULTS: DashboardFilters = {
  clients: null,
  products: null,
  months: null,
  portfolio_managers: null,
  project_managers: null,
  account_owners: null,
  markets: null,
  industry_leaders: null,
  sbus: null,
  product_employees: null,
  portfolio: null,
  sub_portfolios: null,
  risk_statuses: null,
  search: null,
  as_of_date: null,
  include_pass_through_media: false,
  // Draft contracts are counted in the recap by default (user request
  // 2026-06); the toggle is for excluding them.
  include_draft: true,
  revenue_components: null,
  is_churned: null,
};

const FiltersContext = createContext<FiltersContextValue>({
  filters: {},
  setFilters: () => {},
  updateFilters: () => {},
  resetFilters: () => {},
  resetFilter: () => {},
  activeCount: 0,
});

export function FiltersProvider({ children }: { children: React.ReactNode }) {
  const [filters, setFiltersRaw] = useState<DashboardFilters>({
    clients: null,
    products: null,
    months: null,
    portfolio_managers: null,
    project_managers: null,
    account_owners: null,
    markets: null,
    industry_leaders: null,
    sbus: null,
    product_employees: null,
    portfolio: null,
    sub_portfolios: null,
    risk_statuses: null,
    search: null,
    as_of_date: null,
    include_pass_through_media: false,
    include_draft: true,
    revenue_components: null,
    is_churned: null,
  });

  const setFilters = useCallback((f: DashboardFilters) => setFiltersRaw(f), []);

  const updateFilters = useCallback(
    (patch: Partial<DashboardFilters>) => setFiltersRaw((prev) => ({ ...prev, ...patch })),
    [],
  );

  const resetFilters = useCallback(() => {
    setFiltersRaw((prev) => ({
      clients: null,
      products: null,
      months: null,
      portfolio_managers: null,
      project_managers: null,
      account_owners: null,
      markets: null,
      industry_leaders: null,
      sbus: null,
      product_employees: null,
      portfolio: null,
      sub_portfolios: null,
      risk_statuses: null,
      search: null,
      as_of_date: prev.as_of_date ?? null,
      include_pass_through_media: false,
      include_draft: true,
      revenue_components: null,
      is_churned: null,
    }));
  }, []);

  const resetFilter = useCallback((key: keyof DashboardFilters) => {
    setFiltersRaw((prev) => ({ ...prev, [key]: FILTER_DEFAULTS[key] }));
  }, []);

  const activeCount = useMemo(() => {
    let count = 0;
    if (filters.clients?.length) count++;
    if (filters.products?.length) count++;
    if (filters.months?.length) count++;
    if (filters.portfolio_managers?.length) count++;
    if (filters.project_managers?.length) count++;
    if (filters.account_owners?.length) count++;
    if (filters.markets?.length) count++;
    if (filters.industry_leaders?.length) count++;
    if (filters.sbus?.length) count++;
    if (filters.product_employees?.length) count++;
    if (filters.portfolio) count++;
    if (filters.sub_portfolios?.length) count++;
    if (filters.risk_statuses?.length) count++;
    if (filters.search) count++;
    // include_pass_through_media defaults to false; only count when user turned it on.
    if (filters.include_pass_through_media) count++;
    // include_draft defaults to true; only count when user turned it OFF
    // (excluding drafts is the deviation from the default recap).
    if (filters.include_draft === false) count++;
    // revenue_components defaults to null (all visible). Count only when the
    // user actually narrows it — a full 3/3 selection is equivalent to null.
    if (filters.revenue_components && filters.revenue_components.length > 0 && filters.revenue_components.length < 3) {
      count++;
    }
    if (filters.is_churned !== null && filters.is_churned !== undefined) count++;
    return count;
  }, [filters]);

  // Memoize so consumers don't re-render when an ancestor re-renders without
  // the filter state actually changing. Callbacks are useCallback-stable.
  const value = useMemo(
    () => ({ filters, setFilters, updateFilters, resetFilters, resetFilter, activeCount }),
    [filters, setFilters, updateFilters, resetFilters, resetFilter, activeCount],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters() {
  return useContext(FiltersContext);
}
